import { useCallback, useEffect, useState } from 'react';
import { stationTier, type EconomyOverview, type EconomySource } from '@galaxis/shared';
import { fetchEconomyOverview, fetchResources, upgradeStation } from '../api/galaxy.ts';
import { useLiveResources } from '../economy/live-store.ts';
import { useGameStore } from '../store/game-store.ts';
import { constructionName } from './construction-labels.ts';

function remaining(iso: string | null, now: number): string {
  if (!iso) return 'Befejezési idő nélkül';
  const seconds = Math.max(0, Math.ceil((Date.parse(iso) - now) / 1000));
  if (seconds === 0) return 'Befejezés feldolgozás alatt';
  const hours = Math.floor(seconds / 3600), minutes = Math.floor((seconds % 3600) / 60);
  return hours ? `${hours} ó ${minutes} p` : `${minutes} p ${seconds % 60} mp`;
}

export function ConstructionView() {
  const jobs = useGameStore(s => s.activeWork);
  const show = useGameStore(s => s.showSystemOnMap);
  const resources = useGameStore(s => s.resources);
  const setResources = useGameStore(s => s.setResources);
  const pushToast = useGameStore(s => s.pushToast);
  const live = useLiveResources();
  const [economy, setEconomy] = useState<EconomyOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
  const load = useCallback(() => {
    void fetchEconomyOverview().then(value => { setEconomy(value); setError(null); })
      .catch(e => setError(e instanceof Error ? e.message : 'Az állomások nem tölthetők be.'));
  }, []);
  useEffect(() => { load(); }, [load, resources]);
  useEffect(() => { window.addEventListener('focus', load); return () => window.removeEventListener('focus', load); }, [load]);
  const stations = economy?.sources.filter(src => src.kind === 'mining_station' || src.kind === 'energy_station') ?? [];
  const upgrade = async (src: EconomySource) => {
    setBusy(src.id);
    try {
      await upgradeStation(src.id);
      pushToast(`${src.systemName}: ${src.kind === 'mining_station' ? 'bánya' : 'energiaállomás'} fejlesztése elindult`);
      setResources(await fetchResources());
      load();
    } catch (e) {
      pushToast(e instanceof Error ? e.message : 'A fejlesztés nem sikerült', 'error');
    } finally { setBusy(null); }
  };
  return <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="construction-view">
    <header><h1 className="text-xl font-semibold">Építés</h1><p className="text-sm text-slate-400">Folyamatban: {jobs.length} · A rendszerre ugrással az építés részletei is elérhetők.</p></header>
    {jobs.length === 0 ? <p className="sci-fi-panel rounded-xl p-5 text-slate-300">Jelenleg nincs folyamatban építés vagy hajógyártás a birodalmadban.</p> :
      <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{jobs.map(job => <li key={`${job.kind}:${job.id}`} className="sci-fi-panel rounded-xl p-4 space-y-2">
        <p className="font-semibold">🏗️ {constructionName(job.kind)}</p>
        <p className="text-sm text-slate-300">{job.systemName}</p>
        <p className="text-sm text-amber-300" aria-label="Hátralévő idő">{remaining(job.completesAt, now)}</p>
        <button type="button" className="btn-secondary min-h-11" onClick={() => show(job.systemId)}>Mutasd a térképen</button>
      </li>)}</ul>}
    <section aria-labelledby="station-upgrades" className="space-y-3">
      <h2 id="station-upgrades" className="text-lg font-semibold">Bányák és energiaállomások fejlesztése</h2>
      <p className="text-sm text-slate-400">A magasabb szint növeli a termelést, de a fenntartást is. Energiahiánynál az energiaállomás fejlesztése segíthet a bányák működésén.</p>
      {error ? <p role="alert" className="text-sm text-red-400">{error} <button type="button" className="underline" onClick={load}>Újrapróbálás</button></p>
        : !economy || !live ? <p className="text-sm text-slate-400">Állomások betöltése…</p>
        : stations.length === 0 ? <p className="sci-fi-panel rounded-xl p-5 text-slate-300">Még nincs fejleszthető bánya vagy energiaállomás. Előbb építs állomást egy lelőhelyre a galaxistérképen.</p>
        : <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{stations.map(src => {
          const current = stationTier(src.level);
          const next = src.upgrade ? stationTier(src.upgrade.toLevel) : null;
          const affordable = !!src.upgrade && live.metal >= src.upgrade.metal && live.energy >= src.upgrade.energy;
          const upgrading = src.upgradingTo !== null && src.upgradeCompletesAt !== null;
          return <li key={src.id} className="sci-fi-panel rounded-xl p-4 space-y-2" data-testid={`station-upgrade-${src.id}`}>
            <h3 className="font-semibold">{src.kind === 'mining_station' ? 'Bánya' : 'Energiaállomás'} · {src.systemName}</h3>
            <p className="text-sm text-slate-300">{src.depositName ? `${src.depositName} · ` : ''}{src.level}. szint · {Math.round(src.effectivePerHour)}/óra {src.kind === 'mining_station' ? 'fém' : 'energia'}</p>
            {next && <p className="text-xs text-slate-400">Következő szint: termelés ×{next.output.toString().replace('.', ',')} · fenntartás {current.metalUpkeep} → {next.metalUpkeep} fém/óra{src.kind === 'mining_station' ? ` és ${current.miningEnergyUpkeep} → ${next.miningEnergyUpkeep} energia/óra` : ''}</p>}
            {upgrading ? <p role="status" className="text-sm text-amber-300">Fejlesztés → {src.upgradingTo}. szint · {remaining(src.upgradeCompletesAt, now)}</p>
              : src.status !== 'active' ? <p className="text-sm text-red-400">A sérült állomás nem fejleszthető.</p>
              : src.upgrade ? <>
                <p className="text-xs text-slate-400">Költség: {src.upgrade.metal} fém · {src.upgrade.energy} energia</p>
                <button type="button" className="btn-primary min-h-11 disabled:opacity-40" disabled={!affordable || busy !== null} onClick={() => void upgrade(src)}>
                  {busy === src.id ? 'Indítás…' : `Fejlesztés ${src.upgrade.toLevel}. szintre`}
                </button>
                {!affordable && <p className="text-xs text-amber-300">Nincs elég nyersanyag a fejlesztéshez.</p>}
              </> : <p className="text-sm text-slate-400">Elérte a legmagasabb szintet.</p>}
          </li>;
        })}</ul>}
    </section>
  </div>;
}
